import { CheckCircle2, FileText, Info, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';

import { FilePreviewDialog } from '@/components/patterns/FilePreviewDialog';
import { StepIcon, type StepIconTone } from '@/features/workflows/stepIcons';
import { cn } from '@/lib/utils';

import { type StepArtefact, useStepArtefacts } from '../../api/useStepArtefacts';
import type { SessionStepDto } from '../../schemas/sessionSchemas';
import { EmailVerificationResultView } from '../../steps/email/EmailVerificationResultView';
import { isEmailVerificationStep } from '../../steps/email/email.utils';
import { QuestionnaireAnswersView } from '../../steps/questionnaire/QuestionnaireAnswersView';
import { isQuestionnaireStep } from '../../steps/questionnaire/questionnaire.utils';
import { StepOutcomeBadge } from '../statusAxisBadges';
import {
  Accordion,
  KeyValueList,
  KeyValueRow,
  SubAccordion,
  SubCard,
  TwoColumnGrid,
} from './Accordion';
import {
  categoryForStep,
  flagIsOk,
  parseResult,
  type ResolvedField,
  resolveFields,
  stepReason,
} from './returnDataCatalog';

/**
 * Why the step did not pass, stated at the top of its card — the first thing an admin
 * looking at a rejected run needs, and previously nowhere on the page.
 *
 * Red for a rejection (final), orange for a review (a decision is still owed). The wording
 * comes from the backend, so it says the actual cause ("This ID has already been verified
 * in an earlier session") rather than a generic failure line.
 */
function ReasonBanner({ reason, status }: { reason: string; status: string }) {
  const rejected = status === 'Failed';
  const color = rejected ? '#BF3636' : '#F27310';
  const Icon = rejected ? XCircle : Info;
  return (
    <div
      className="flex items-start gap-2.5 rounded-lg border p-[clamp(10px,0.9vw,14px)]"
      style={{ borderColor: `${color}80`, backgroundColor: `${color}0D`, color }}
    >
      <Icon size={16} className="mt-px shrink-0" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[length:var(--text-14)] font-semibold">
          {rejected ? 'Why this was rejected' : 'Why this needs review'}
        </span>
        <span className="text-[length:var(--text-14)] font-medium text-[#303030]">{reason}</span>
      </div>
    </div>
  );
}

// Step outcome → icon tint, the same mapping the sessions list uses for its icon flow.
const TONE_BY_STATUS: Record<string, StepIconTone> = {
  Passed: 'green',
  Failed: 'red',
  Review: 'orange',
  Pending: 'neutral',
};

/**
 * A `checks` field's value: a glyph for flags and statuses, coloured text for a score,
 * plain text otherwise — the design's Check List column.
 */
function CheckValue({ rf, stepStatus }: { rf: ResolvedField; stepStatus: string }) {
  const { field, raw, text } = rf;

  if (field.display === 'flag') {
    return flagIsOk(field, raw) ? (
      <CheckCircle2 size={16} className="shrink-0 text-[#10B981]" />
    ) : (
      <XCircle size={16} className="shrink-0 text-[#BF3636]" />
    );
  }
  if (field.display === 'status') {
    if (stepStatus === 'Passed')
      return <CheckCircle2 size={16} className="shrink-0 text-[#10B981]" />;
    if (stepStatus === 'Failed') return <XCircle size={16} className="shrink-0 text-[#BF3636]" />;
    return <Info size={16} className="shrink-0 text-[#F27310]" />;
  }
  if (field.display === 'score') {
    return <span className="font-semibold text-[#00A92C]">{text}</span>;
  }
  return <span className="truncate">{text}</span>;
}

/**
 * A rule key → the label an admin recognises.
 *
 * Falls back to de-slugging the key (`pan_already_exists` → "Pan Already Exists") rather
 * than hiding an unmapped rule: a rule that governed the run must appear, and a readable
 * approximation is better than dropping it silently when a new rule ships.
 */
const RULE_LABELS: Record<string, string> = {
  pan_already_exists: 'PAN Already Exists in System',
  duplicate_pan_flag: 'Duplicate PAN Flag',
  pan_aadhaar_linked: 'PAN-Aadhaar Linking Status',
  pan_status: 'PAN Status',
  name_match_score: 'Name Match Score',
  age_restriction: 'Age Restriction',
  aadhaar_already_exists: 'Aadhaar Already Exists in System',
  gstin_already_exists: 'GSTIN Already Exists in System',
  bank_account_already_exists: 'Bank Account Already Exists in System',
  passport_already_exists: 'Passport Already Exists in System',
  ration_card_already_exists: 'Ration Card Already Exists in System',
  udyam_already_exists: 'Udyam Number Already Exists in System',
};

function ruleLabel(ruleKey: string): string {
  if (RULE_LABELS[ruleKey]) return RULE_LABELS[ruleKey];
  // Strip a document scope prefix ("passport::passport_already_exists") before de-slugging.
  const bare = ruleKey.includes('::') ? ruleKey.slice(ruleKey.lastIndexOf('::') + 2) : ruleKey;
  return bare
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const RULE_ACTION_COLOR: Record<string, string> = {
  Approve: '#15803D',
  Review: '#1D4ED8',
  Reject: '#B91C1C',
};

function RuleActionBadge({ action }: { action: string }) {
  const color = RULE_ACTION_COLOR[action] ?? '#4B5563';
  const Icon = action === 'Reject' ? XCircle : action === 'Review' ? Info : CheckCircle2;
  return (
    <span
      className="flex items-center gap-1.5 px-3 py-0.5 text-[length:clamp(11px,0.75vw,13px)] font-medium"
      style={{ color }}
    >
      <Icon size={14} className="shrink-0" />
      <span>{action}</span>
    </span>
  );
}

/**
 * One verification step as the design's accordion: the step's name and outcome in the
 * header, and in the body its Detail card beside its Check List card, then any uploaded
 * documents, then the Risk Rules that governed it.
 *
 * The Detail/Check split is not invented here — `returnDataCatalog` already tags every
 * field with `panel: 'details' | 'checks'`, which is the same division the design draws.
 */
export function StepAccordion({
  step,
  sessionId,
  open,
  onOpenChange,
  titleOverride,
}: {
  step: SessionStepDto;
  /** Needed to fetch the step's stored artefacts. */
  sessionId: string | undefined;
  /** Controlled by the screen, so a header chip can expand this step as well as scroll to it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Replaces `step.displayName` in the header when the stored name is generic — a questionnaire step's
   * real name. See useSessionQuestionnaireNames.
   */
  titleOverride?: string;
}) {
  const spec = categoryForStep(step);
  const result = useMemo(() => parseResult(step.resultJson), [step.resultJson]);
  const details = useMemo(
    () => (spec ? resolveFields(spec, result, 'details') : []),
    [spec, result],
  );
  const checks = useMemo(() => (spec ? resolveFields(spec, result, 'checks') : []), [spec, result]);
  const reason = stepReason(step);

  // Only while this step is expanded — see useStepArtefacts for why the presigned URLs are
  // not minted for steps nobody has opened.
  const { data: artefacts } = useStepArtefacts(sessionId, step.id, open);

  // A Questionnaire step has no API catalogue entry, so `returnDataCatalog` resolves no fields for it
  // and the generic Detail / Check List cards below stay empty. Its body is its ANSWERS instead.
  const isQuestionnaire = isQuestionnaireStep(step);

  // Email Verification is in the same position and for the same reason: WE send the code, so there
  // is no catalogue entry to resolve fields from. Its body is the address we confirmed.
  const isEmailVerification = isEmailVerificationStep(step);

  const hasBody = isQuestionnaire || isEmailVerification || details.length > 0 || checks.length > 0;

  return (
    <Accordion
      // The workflow step icon — the same masked SVG the builder sidebar and the sessions
      // list use, tinted by outcome. A lucide stand-in here meant PAN (say) was drawn with
      // one glyph in the list and a different one on this page.
      icon={<StepIcon stepType={step.stepType} tone={TONE_BY_STATUS[step.status] ?? 'neutral'} />}
      title={titleOverride ?? step.displayName}
      right={<StepOutcomeBadge status={step.status} />}
      open={open}
      onOpenChange={onOpenChange}
    >
      {/* First in the body: a rejected or under-review step leads with WHY. */}
      {reason && <ReasonBanner reason={reason} status={step.status} />}

      {isQuestionnaire ? (
        <QuestionnaireAnswersView
          sessionId={sessionId}
          stepId={step.id}
          inputJson={step.inputJson}
        />
      ) : isEmailVerification ? (
        <EmailVerificationResultView resultJson={step.resultJson} />
      ) : hasBody ? (
        <TwoColumnGrid>
          {details.length > 0 && (
            <SubCard title={`${spec?.label ?? step.displayName} Detail`}>
              <KeyValueList>
                {details.map((rf) => (
                  <KeyValueRow key={rf.field.key} label={`${rf.field.label}:`} value={rf.text} />
                ))}
              </KeyValueList>
            </SubCard>
          )}

          {checks.length > 0 && (
            <SubCard title="Check List">
              <KeyValueList>
                {checks.map((rf) => (
                  <KeyValueRow
                    key={rf.field.key}
                    label={`${rf.field.label}:`}
                    value={<CheckValue rf={rf} stepStatus={step.status} />}
                  />
                ))}
              </KeyValueList>
            </SubCard>
          )}
        </TwoColumnGrid>
      ) : (
        // Suppressed when a reason is shown. A step rejected before the provider was even
        // called stores nothing but its reason, so "No result data was returned" would sit
        // directly under an explanation and flatly contradict it.
        !reason && (
          <p className="text-[length:var(--text-14)] text-[#666666]">
            {!spec
              ? 'This step type has no detailed view yet.'
              : step.status === 'Pending'
                ? 'This step has not been completed yet — no result to show.'
                : 'No result data was returned for this step.'}
          </p>
        )
      )}

      {artefacts && artefacts.length > 0 && <StepArtefactGallery artefacts={artefacts} />}

      {/* ALWAYS rendered, rules or none.
          It used to be hidden when the step had no rules, which made the section's absence
          ambiguous in the one place ambiguity is expensive: a reviewer could not tell "this
          step was governed by no rules" from "the rules are not shown here". Those are very
          different facts to sign a KYC decision against. It also made every step a different
          height, so the page rearranged itself as you opened them. */}
      {/* Collapsed by default, on every step of every session.
          Rules are configuration, not a result: they say what the workflow was set up to do,
          which is identical for every session on that workflow and does not change with the
          applicant. Expanded, five rows of that pushed the NEXT step's card off the screen —
          so reading a five-step run meant scrolling past the same list five times to reach
          the findings, which are the part that differs. The header still says the section is
          there, one click away, for the times a reviewer needs to check what was in force. */}
      <SubAccordion title="Risk Rules">
        {step.riskRules.length === 0 ? (
          // Green, because no rules in force is a clean state, not a warning or a gap. It
          // says what it means literally — the step was still evaluated, there was simply
          // nothing extra to apply beyond the provider's own verdict.
          <div className="flex items-start gap-2 rounded-lg border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2.5">
            <CheckCircle2 size={16} className="mt-px shrink-0 text-[#15803D]" />
            <div className="min-w-0">
              <p className="text-[length:var(--text-14)] font-medium text-[#15803D]">
                No risk rules
              </p>
              <p className="mt-0.5 text-[length:var(--text-12)] leading-snug text-[#166534]">
                No risk rules were configured for this step, so its outcome comes from the
                verification result alone.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {step.riskRules.map((rule) => (
                // Label column, then the badge immediately after it — the design's
                // `.sc-risk-rule-item` is a plain flex with a min-width label, so the badge
                // sits beside the name rather than against the card's right edge.
                <div
                  key={rule.ruleKey}
                  className="flex items-center gap-3 text-[length:var(--text-14)]"
                >
                  <span className="min-w-[clamp(180px,15vw,220px)] shrink-0 font-normal text-[#323338]">
                    {ruleLabel(rule.ruleKey)}
                  </span>
                  <RuleActionBadge action={rule.action} />
                </div>
              ))}
            </div>
            {/* Stated plainly, because the badges look like verdicts and are not. Only the
                step's aggregate outcome is recorded, so claiming per-rule results here
                would be an audit trail we cannot back up. */}
            <p className="mt-3 text-[length:var(--text-12)] text-[#666666]">
              These are the rules that were in force for this step and what each was set to do.
              Per-rule outcomes are not recorded — the step&apos;s own status above is the combined
              result.
            </p>
          </>
        )}
      </SubAccordion>
    </Accordion>
  );
}

/**
 * The designer's DocumentGallery — the step's stored images side by side, with the selected
 * one enlarged and a thumbnail strip beneath.
 *
 * Ported from `DocumentGallery.tsx` / `.css`: title `text-16 font-semibold #041A3A`, preview
 * cards `aspect-[290/182]` with a `#E4E4E4` border and a `#0048FF` ring when active,
 * thumbnails a fixed 70x44 with a 2px `#0048FF` border when active.
 *
 * TWO departures from that mock, both because it was drawn against fake data:
 *
 *   • Its trailing dashed "+" tile is omitted. It adds nothing here — an admin cannot upload
 *     an artefact into a completed verification, and a control that looks live but does
 *     nothing is worse than no control.
 *   • Every image is CAPTIONED. The mock shows an anonymous strip of identical documents; a
 *     real Face Match step shows the applicant's selfie next to the Aadhaar photo it was
 *     compared against, and those two being confusable is the whole point of looking.
 */
function StepArtefactGallery({ artefacts }: { artefacts: StepArtefact[] }) {
  const [active, setActive] = useState(0);
  /**
   * The file open in the shared preview dialog, or null.
   *
   * Holds the FILE rather than an index: the dialog outlives a refetch that reorders or shortens
   * the list, and an index would then be pointing at a different document than the one the reviewer
   * opened.
   */
  const [preview, setPreview] = useState<StepArtefact | null>(null);
  // The list can shrink between renders (a refetch after the URLs expired), so the index is
  // clamped rather than trusted — an out-of-range `active` would blank the preview.
  const current = artefacts[Math.min(active, artefacts.length - 1)];
  if (!current) return null;

  const images = artefacts.filter((a) => !a.isPdf);
  const pdfs = artefacts.filter((a) => a.isPdf);

  return (
    <SubCard title="Documents & Photos">
      {images.length > 0 && (
        <>
          {/* All images side by side, as the designer draws it — with Face Match that IS the
              comparison, so putting the selfie and the reference in one row is the feature
              rather than a layout choice. */}
          <div className="flex flex-wrap items-start gap-3">
            {images.map((a) => (
              <button
                key={a.url}
                type="button"
                // Selects AND opens. With one image there is nothing to select, so a click that
                // only highlighted it did nothing visible; the thumbnail row below remains the way
                // to switch between several without opening any.
                onClick={() => {
                  setActive(artefacts.indexOf(a));
                  setPreview(a);
                }}
                title={`${a.label} — click to open`}
                // `group` + `outline-none` so the ring can live on the IMAGE instead of the
                // button. The button's box includes the caption, so any outline on it — the
                // selection ring or the browser's own focus ring — drew a line straight
                // through the label text. Focus is not lost: it moves to the image below via
                // `group-focus-visible`.
                // `min()` caps the tile at the CONTAINER width, not just at 290px: on a narrow
                // phone the old `clamp(180px, …)` floor was wider than the card's content box, so a
                // second image pushed the row into a horizontal overflow.
                className="group flex w-full shrink-0 flex-col gap-1.5 text-left outline-none sm:w-[min(100%,290px)]"
              >
                <span
                  className={cn(
                    'aspect-[290/182] w-full overflow-hidden rounded-xl border border-[#E4E4E4] bg-[#F9FAFB] transition-all',
                    // No selection ring on a lone image — there is nothing to select between,
                    // so a blue outline reads as a state the reviewer put it in.
                    images.length > 1 && a === current ? 'ring-2 ring-[#0048FF]/80' : '',
                    'group-focus-visible:ring-2 group-focus-visible:ring-[#0048FF]',
                  )}
                >
                  {/* `object-contain`, NOT the designer's `object-cover`. Their mock uses
                      landscape document scans; ours are portrait face crops from an Aadhaar,
                      and `cover` on one of those crops the top of the head off — which is the
                      one part of a face photo a reviewer is comparing. */}
                  <img
                    src={a.url}
                    alt={a.label}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                </span>
                {/* Caption only when there is more than one image.
                    On a single photo it was pure repetition — the card is titled
                    "Documents & Photos" and there is one thing in it, so "Photo on record"
                    told the reviewer nothing they could not see. With TWO it is load-bearing:
                    Face Match shows the applicant's selfie beside the Aadhaar photo it was
                    compared against, and an unlabelled pair of similar faces is exactly the
                    thing a reviewer must not have to guess at. `title` keeps the name
                    reachable on hover in both cases. */}
                {images.length > 1 && (
                  <span className="text-[length:var(--text-12)] font-medium text-[#4B5563]">
                    {a.label}
                  </span>
                )}
              </button>
            ))}
          </div>

          {images.length > 1 && (
            <div className="mt-1 flex flex-wrap items-center gap-3">
              {images.map((a) => (
                <button
                  key={`thumb-${a.url}`}
                  type="button"
                  onClick={() => setActive(artefacts.indexOf(a))}
                  title={a.label}
                  aria-label={a.label}
                  className={cn(
                    // Square-ish, not the designer's 70x44 landscape. Their thumbnails are of
                    // document scans; ours are portrait face crops, and a 70x44 letterbox
                    // leaves a face a 15px-tall sliver — which is what made the Aadhaar
                    // thumbnail unreadable.
                    'h-[52px] w-[52px] shrink-0 overflow-hidden rounded-lg bg-[#F9FAFB] transition-all hover:opacity-90',
                    a === current ? 'border-2 border-[#0048FF]' : 'border border-[#D1D5DB]',
                  )}
                >
                  {/* `contain`, matching the large preview above. `cover` filled the tile but
                      cropped the head — and a thumbnail whose job is "which of these two
                      faces" must not cut off the face. */}
                  <img src={a.url} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* A PDF cannot go in an <img>, so it opens in the SHARED preview dialog rather than a new
          tab — a signed URL in a new tab loses the session the reviewer is reading, along with
          their scroll position and which steps they had expanded. Download and open-in-tab live
          inside that dialog for the cases a browser will not render a PDF inline.

          A BUTTON, not an anchor: it no longer navigates. */}
      {pdfs.map((a) => (
        <button
          key={a.url}
          type="button"
          onClick={() => setPreview(a)}
          title={`${a.label} — click to open`}
          // Full width on a phone (`max-w-max` made a long filename overflow the card instead of
          // truncating), settling to content width from `sm` up.
          className="mt-2 flex w-full items-center gap-2 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] px-3 py-2 text-left text-[length:var(--text-14)] font-medium text-[#0048FF] transition-colors hover:bg-[#DBEAFE] sm:w-auto sm:max-w-full"
        >
          <FileText size={15} className="shrink-0" />
          {/* `min-w-0` + `truncate`: the filename shortens rather than pushing the row wider than
              its container, which is what "SignCare_Studio_Final_v2 (1).pdf" did on a phone. */}
          <span className="min-w-0 truncate">{a.label}</span>
        </button>
      ))}

      <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />
    </SubCard>
  );
}
